import { rem } from 'polished';
import React, { useState } from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import { css } from 'styled-components';
import Box from '../Box/Box';
import Collapse from '../Collapse/Collapse';

const CollapseContainerContext = React.createContext({
    open: true,
    setOpen: (bool) => {},
});

function useCollapseContainerContext() {
    const context = React.useContext(CollapseContainerContext);
    if (!context) {
        throw new Error(
            `Collapse Container compound components cannot be rendered outside the Collapse Card component`
        );
    }
    return context;
}

interface CollapseContainerInterface<T> extends React.FC<T> {
    Arrow: React.FC;
    Item: React.FC;
}

interface CollapseContainerProps {
    initiallyOpen?: boolean;
}

const CollapseContainer: CollapseContainerInterface<CollapseContainerProps> = ({
    initiallyOpen = false,
    children,
    ...props
}) => {
    const [open, setOpen] = useState(initiallyOpen);
    return (
        <Box {...props}>
            <CollapseContainerContext.Provider value={{ open, setOpen }}>
                {children}
            </CollapseContainerContext.Provider>
        </Box>
    );
};

const Arrow = ({ size, color, ...props }: { size?: number; color: string }) => {
    const { open, setOpen } = useCollapseContainerContext();
    return (
        <button
            type="button"
            onClick={() => setOpen(!open)}
            style={{ display: 'flex', justifyContent: 'end' }}
            {...props}
        >
            <IoIosArrowDown
                css={`
                    transition: transform 250ms cubic-bezier(0, 0, 0.16, 0.96)
                        0s;
                    transform: rotate(${open ? -180 : 0}deg);
                    cursor: pointer;
                    color: ${({ theme }) => theme.colors.primary[1800]};
                    height: 24px;
                `}
                size={size}
                color={color}
            />
        </button>
    );
};

const Item = ({ children, ...props }) => {
    const { open } = useCollapseContainerContext();
    return (
        <Collapse {...props} open={open}>
            {children}
        </Collapse>
    );
};

const Header = ({ children, onClick, ...props }) => {
    const { open, setOpen } = useCollapseContainerContext();
    return (
        <Box
            cursor="pointer"
            onClick={() => {
                setOpen(!open);
            }}
            {...props}
        >
            {children}
        </Box>
    );
};

CollapseContainer.Arrow = Arrow;
CollapseContainer.Header = Header;
CollapseContainer.Item = Item;

export default CollapseContainer;
